import React from 'react';
import { Box, Typography, Stepper, Step, StepIndicator } from '@mui/joy';
import { Check } from '@phosphor-icons/react';
import { SETUP_STEPS, isStepCompleted } from '../../types/setup';
import type { SetupStepId, SetupProgress } from '../../types/setup';

interface SetupProgressIndicatorProps {
  currentStep: SetupStepId;
  progress: SetupProgress;
  onStepClick?: (stepId: SetupStepId) => void;
}

export const SetupProgressIndicator: React.FC<SetupProgressIndicatorProps> = ({
  currentStep,
  progress,
  onStepClick,
}) => {
  const currentIndex = SETUP_STEPS.findIndex((s) => s.id === currentStep);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', pb: 1 }}>
      <Stepper
        size="sm"
        sx={{
          width: '100%',
          minWidth: 'fit-content',
          '--Stepper-verticalGap': '8px',
          '--StepIndicator-size': '28px',
          '--Step-gap': '8px',
          '--Step-connectorInset': '0px',
          '--Step-connectorRadius': '4px',
          '--Step-connectorThickness': '2px',
        }}
      >
        {SETUP_STEPS.map((step, index) => {
          const isCompleted = isStepCompleted(progress, step.id);
          const isCurrent = step.id === currentStep;
          const isClickable = !!onStepClick;

          return (
            <Step
              key={step.id}
              completed={isCompleted}
              active={isCurrent}
              indicator={
                <StepIndicator
                  variant={isCompleted ? 'solid' : isCurrent ? 'soft' : 'outlined'}
                  color={isCompleted ? 'success' : isCurrent ? 'primary' : 'neutral'}
                  sx={{
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable
                      ? {
                          boxShadow: 'sm',
                        }
                      : {},
                  }}
                  onClick={() => onStepClick?.(step.id)}
                >
                  {isCompleted ? <Check size={16} /> : index + 1}
                </StepIndicator>
              }
              sx={{
                '&::after': {
                  bgcolor: isCompleted ? 'success.500' : 'neutral.300',
                },
              }}
            >
              <Typography
                level="body-xs"
                sx={{
                  color: isCurrent ? 'primary.600' : isCompleted ? 'success.600' : 'neutral.500',
                  fontWeight: isCurrent ? 600 : 400,
                  whiteSpace: 'nowrap',
                  cursor: isClickable ? 'pointer' : 'default',
                  '&:hover': isClickable
                    ? {
                        color: 'var(--brand-text, #7013f5)',
                      }
                    : {},
                }}
                onClick={() => onStepClick?.(step.id)}
              >
                {step.shortTitle}
              </Typography>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default SetupProgressIndicator;
